import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {InvoiceItem} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ The old config rendered a "Created" column from `item.created`. There is
// no `created` field on the InvoiceItem schema and the query never selected
// one, so that column rendered the em-dash placeholder for every row in every
// environment. The real timestamp is `date: Int!` — see types.ts.
export const useInvoiceItemsTableColumns = () => {

    const columns: DataTableColumn<InvoiceItem>[] = [
        {
            accessor: 'description',
            sortable: true,
            title: I18n.get('Invoice Item'),
            width: '1fr',
            // Nullable on the schema — an item may carry only a price. Falls
            // back to the id so the row is never blank (§13).
            render: (item: InvoiceItem) => item.description ?? item.id,
        },
        {
            // Sorts on the raw minor-unit integer, NOT the formatted string.
            // Sorting the rendered "12.5 usd" would compare text and put
            // "10" before "9" — the exact bug sortRecords' numeric path avoids.
            accessor: 'amount',
            sortable: true,
            title: I18n.get('Amount'),
            width: '1fr',
            // Stripe amounts are in MINOR UNITS (cents), hence /100.
            render: (item: InvoiceItem) =>
                `${(item.amount ?? 0) / 100} ${item.currency ?? ''}`.trim(),
        },
        {
            accessor: 'quantity',
            sortable: true,
            title: I18n.get('Quantity'),
            width: '1fr',
            render: (item: InvoiceItem) => item.quantity ?? '—',
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `date` (seconds) is still the source.
            accessor: 'dateAtMillis',
            sortable: true,
            title: I18n.get('Date'),
            width: '1fr',
            render: (item: InvoiceItem) => formatTimestamp(item.dateAtMillis),
        },
    ];

    return {columns};
};
